import React from 'react'
import { Region, XP_REQUEST_MODE, type ComponentProps, type LayoutData } from '@enonic/react-components'
import SubpageHeroPrototype from '/react4xp/components/prototype/SubpageHero'
import OpenPositionCardPrototype from '/react4xp/components/prototype/OpenPositionCard'
import PaginationPrototype from '/react4xp/components/prototype/Pagination'
import type { OpenPositionCardProps } from '/react4xp/components/prototype/OpenPositionCard/types'
import type { SubpageHeroFilter } from '/react4xp/components/prototype/SubpageHero/types'

/**
 * Job Offerings layout wrapping the styled prototype components.
 *
 * Filter and pagination callbacks update the query string so the server reruns
 * the processor; edit mode keeps those controls inert.
 *
 * This is a Layout (user decision, 2026-08-24), so the `main` Region renders
 * unconditionally - including when zero positions match. Gating it behind
 * `positions.length` would leave editors unable to drop anything into the region.
 *
 * The layout had an `intro` config field that was never drawn - SubpageHero has no slot for
 * it and the delivered prototype draws overline, title and dropdown only - so it was removed
 * from the schema, the processor and here (I99X-418, Ana 2026-08-26). Each position's own
 * `item.intro` is a different field and still feeds the card description.
 */
type FilterOption = {
	value: string
	label: string
}

type JobOfferingItem = {
	href: string | null
	title: string
	intro?: string
	location: FilterOption
}

type Pagination = {
	currentPage: number
	totalPages: number
	pageSize: number
	totalItems: number
	hasPrevious: boolean
	hasNext: boolean
}

type JobOfferingsStrings = {
	locationFilter: string
	allCountries: string
	other: string
	readMore: string
	emptyMessage: string
}

type JobOfferingsLayoutData = {
	overline?: string
	title?: string
	items?: JobOfferingItem[]
	filters?: { locations: FilterOption[] }
	selectedFilters?: { location: string | null }
	strings?: JobOfferingsStrings
	pagination?: Pagination
}

// The processor already returns `all` as the first filter option, so unlike
// ArticleList this view neither synthesises one nor needs a sentinel value.
const ALL_VALUE = 'all'

// OpenPositionCard's readMoreUrl is non-nullable, so a position whose page did
// not resolve is dropped rather than rendered as a dead card.
function toPosition(item: JobOfferingItem, readMoreLabel?: string): OpenPositionCardProps | null {
	if (!item.href) return null

	return {
		country: item.location.label,
		title: item.title,
		description: item.intro || '',
		readMoreUrl: item.href,
		readMoreLabel,
	}
}

export const JobOfferings = ({ component, data, meta }: ComponentProps<LayoutData>) => {
	const {
		overline,
		title,
		items = [],
		filters,
		selectedFilters,
		strings,
		pagination,
	} = (data || {}) as JobOfferingsLayoutData

	// Navigation must remain inert inside Content Studio's editor frame.
	const isEditMode = meta?.mode === XP_REQUEST_MODE.EDIT

	function navigate(params: Record<string, string | null>) {
		if (isEditMode || typeof window === 'undefined') return

		const url = new URL(window.location.href)
		Object.keys(params).forEach((key) => {
			const value = params[key]
			if (value) {
				url.searchParams.set(key, value)
			} else {
				url.searchParams.delete(key)
			}
		})
		window.location.assign(url.toString())
	}

	// Reset paging after filtering so a smaller result set cannot open past its end.
	const locationOptions = filters?.locations || []
	const heroFilters: SubpageHeroFilter[] = locationOptions.length > 0
		? [
			{
				id: 'location',
				label: strings?.locationFilter || '',
				options: locationOptions,
				value: selectedFilters?.location || ALL_VALUE,
				onChange: (value: string) => navigate({ location: value === ALL_VALUE ? null : value, page: null }),
			},
		]
		: []

	const positions = items.map((item) => toPosition(item, strings?.readMore)).filter((position): position is OpenPositionCardProps => position !== null)
	const currentPage = pagination?.currentPage || 1
	const totalPages = Math.max(pagination?.totalPages || 0, 1)

	return (
		<>
			<SubpageHeroPrototype overline={overline} heading={title || ''} filters={heroFilters} />

			{/* Mirrors prototype/src/components/JobOfferings/index.tsx; pagination hidden on a single page. */}
			<section className="my-12">
				<div className="container flex flex-col gap-8">
					{positions.length > 0 ? (
						<>
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-8">
								{positions.map((position, i) => (
									<OpenPositionCardPrototype key={i} {...position} />
								))}
							</div>
							{totalPages > 1 && (
								<PaginationPrototype
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={(page: number) => navigate({ page: page > 1 ? String(page) : null })}
								/>
							)}
						</>
					) : (
						<p className="body text-black-950 text-center">{strings?.emptyMessage}</p>
					)}
				</div>
			</section>
			<Region name="main" data={component?.regions?.main?.components || []} meta={meta} />
		</>
	)
}
